import React, { useMemo, useState } from "react";
import { isPresent, isLate, isAbsent } from "./StaffAnalyticsTab";
import TabKpiRow from "./TabKpiRow";

const isExpected = (att) =>
  att?.status_value !== "on_leave" && att?.status_value !== "day_off";

const computeScores = (shifts) => {
  const map = {};
  shifts.forEach(({ gymId, gymName, att, userId, name, accountType }) => {
    if (!map[gymId]) map[gymId] = { gymId, gymName, exp: 0, pres: 0, onTime: 0, late: 0, absent: 0, staff: {} };
    const g = map[gymId];
    if (!isExpected(att)) return;
    g.exp++;

    if (!g.staff[userId]) g.staff[userId] = { name, accountType, late: 0, absent: 0, present: 0 };
    if (isPresent(att)) {
      g.pres++;
      g.staff[userId].present++;
      if (!isLate(att)) g.onTime++;
      else { g.late++; g.staff[userId].late++; }
    } else if (isAbsent(att)) {
      g.absent++;
      g.staff[userId].absent++;
    }
  });

  return Object.values(map).map((g) => {
    const attRate   = g.exp  > 0 ? (g.pres   / g.exp)  * 100 : 0;
    const punctRate = g.pres > 0 ? (g.onTime / g.pres)  * 100 : 100;
    const score     = Math.round(attRate * 0.6 + punctRate * 0.4);
    return { ...g, attRate: Math.round(attRate), punctRate: Math.round(punctRate), score };
  }).sort((a, b) => a.score - b.score);
};

const scoreCircleClass = (s) =>
  s >= 80 ? "sta-score-circle--green" : s >= 60 ? "sta-score-circle--yellow" : "sta-score-circle--red";

const computeHealthKpis = (scores, prevMap) => {
  const healthy  = scores.filter((g) => g.score >= 80).length;
  const atRisk   = scores.filter((g) => g.score >= 60 && g.score < 80).length;
  const critical = scores.filter((g) => g.score < 60).length;
  const avg      = scores.length > 0 ? Math.round(scores.reduce((s, g) => s + g.score, 0) / scores.length) : 0;
  const prevVals = Object.values(prevMap);
  const prevAvg  = prevVals.length > 0 ? Math.round(prevVals.reduce((s, v) => s + v, 0) / prevVals.length) : null;
  const avgDelta = prevAvg != null ? { text: `${avg - prevAvg >= 0 ? "+" : ""}${avg - prevAvg} vs prev`, up: avg >= prevAvg } : undefined;
  return [
    { label: "Healthy",   value: healthy,  mod: healthy > 0 ? "green" : "blue" },
    { label: "At Risk",   value: atRisk,   mod: atRisk  > 0 ? "orange" : "green" },
    { label: "Critical",  value: critical, mod: critical > 0 ? "red"   : "green" },
    { label: "Avg Score", value: avg,      mod: avg >= 80 ? "green" : avg >= 60 ? "orange" : "red", delta: avgDelta },
  ];
};

const AnalyticsGymHealthScores = ({ allShifts = [], prevShifts = [] }) => {
  const [expandedGym, setExpandedGym] = useState(null);

  const currScores = useMemo(() => computeScores(allShifts), [allShifts]);
  const prevMap    = useMemo(() => {
    const m = {};
    computeScores(prevShifts).forEach((g) => { m[g.gymId] = g.score; });
    return m;
  }, [prevShifts]);

  const kpiCards = useMemo(() => computeHealthKpis(currScores, prevMap), [currScores, prevMap]);

  if (currScores.length === 0) {
    return <div className="sta-empty">No gym data available.</div>;
  }

  return (
    <div>
      <TabKpiRow cards={kpiCards} />
      <div className="sta-gym-grid">
      {currScores.map((g) => {
        const prev      = prevMap[g.gymId];
        const delta     = prev != null ? g.score - prev : null;
        const isExpanded = expandedGym === g.gymId;

        const problemStaff = Object.values(g.staff)
          .filter((s) => s.late > 0 || s.absent > 0)
          .sort((a, b) => (b.late + b.absent) - (a.late + a.absent))
          .slice(0, 5);

        return (
          <div
            key={g.gymId}
            className={`sta-gym-card${isExpanded ? " sta-gym-card--expanded" : ""}`}
            style={{ cursor: "pointer" }}
            onClick={() => setExpandedGym(isExpanded ? null : g.gymId)}
          >
            <div className="sta-gym-card__name" title={g.gymName}>
              {g.gymName}
              <span className={`sta-chevron${isExpanded ? " sta-chevron--open" : ""}`}>▼</span>
            </div>

            <div className="sta-score-ring">
              <div className={`sta-score-circle ${scoreCircleClass(g.score)}`}>{g.score}</div>
              <div style={{ fontSize: 12, color: "#555", lineHeight: 1.5 }}>
                <div style={{ fontWeight: 600 }}>
                  {g.score >= 80 ? "Healthy" : g.score >= 60 ? "At Risk" : "Critical"}
                </div>
                {delta != null ? (
                  <div className={delta >= 0 ? "sta-delta--up" : "sta-delta--down"} style={{ fontSize: 11 }}>
                    {delta >= 0 ? `↑ +${delta}` : `↓ ${delta}`} vs prev
                  </div>
                ) : (
                  <div style={{ color: "#ccc", fontSize: 11 }}>No prev data</div>
                )}
              </div>
            </div>

            <div className="sta-gym-card__stats">
              <div>Attendance: <strong>{g.attRate}%</strong></div>
              <div>Punctuality: <strong>{g.punctRate}%</strong></div>
              <div>Late: {g.late} | Absent: {g.absent}</div>
            </div>

            {isExpanded && problemStaff.length > 0 && (
              <div className="sta-gym-accordion">
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Problem staff this period:</div>
                {problemStaff.map((s) => (
                  <div key={s.name} className="sta-gym-accordion__row">
                    <span>{s.name} <span style={{ color: "#aaa", fontSize: 10 }}>({s.accountType === "trainer" ? "Trainer" : "Sales"})</span></span>
                    <span style={{ fontSize: 11 }}>
                      {s.late > 0 && <span style={{ color: "#fd7e14", marginRight: 6 }}>{s.late} late</span>}
                      {s.absent > 0 && <span style={{ color: "#dc3545" }}>{s.absent} absent</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {isExpanded && problemStaff.length === 0 && (
              <div className="sta-gym-accordion" style={{ color: "#28a745", fontWeight: 500 }}>
                No late or absent staff — fully healthy!
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default AnalyticsGymHealthScores;
